/**
 * AppModal — wrapper around React Native's <Modal> that pre-supplies the
 * supportedOrientations prop covering all four iOS orientations.
 *
 * Why this exists: an iOS <Modal> that opens in PORTRAIT and gets rotated
 * to LANDSCAPE while it's open keeps its internal coordinate system in
 * the open-time orientation unless it opts into every orientation it
 * should follow. Without supportedOrientations, button text inside a
 * landscape-opened-while-portrait modal renders sideways. Setting this
 * once on every Modal in the app is error-prone, so every new Modal
 * should import AppModal instead of `react-native`'s Modal directly.
 *
 * On Android the orientation prop is ignored (the OS handles rotation).
 * Transparent game overlays also reserve the navigation bar's space through
 * GameOverlayTopContext on phones and tablets. On desktop they don't: the
 * modal's own backdrop darkens the top bar along with the rest of the board
 * (maintainer, 2026-10-28). EdgeSheet owns its kept edges and opts out.
 *
 * Any prop on RN's Modal is forwarded. Override supportedOrientations to
 * to lock a Modal to portrait (e.g. a one-handed-mode picker).
 */

import React, { useContext } from 'react'
import { VisualDiagnosticsContext } from '../../lib/visualDiagnostics'
import { Modal, View, useWindowDimensions, type ModalProps } from 'react-native'
import { GameOverlayTopContext } from '../../lib/gameOverlayBounds'
import { DESKTOP_BREAKPOINT } from '../../lib/mapDimensions'

const DEFAULT_ORIENTATIONS: NonNullable<ModalProps['supportedOrientations']> = [
  'portrait',
  'portrait-upside-down',
  'landscape',
  'landscape-left',
  'landscape-right',
]

export function AppModal({
  supportedOrientations = DEFAULT_ORIENTATIONS,
  preserveGameTopBar = true,
  ...rest
}: ModalProps & { preserveGameTopBar?: boolean }) {
  const diagnostics = useContext(VisualDiagnosticsContext)
  const gameTop = useContext(GameOverlayTopContext)
  const desktop = useWindowDimensions().width >= DESKTOP_BREAKPOINT
  const children = rest.transparent && preserveGameTopBar && gameTop > 0 && !desktop ? (
    <View style={{ flex: 1, paddingTop: gameTop }}>
      <View pointerEvents="none" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: gameTop, backgroundColor: 'rgba(0,0,0,0.4)' }} />
      <View style={{ flex: 1, overflow: 'hidden' }}>{rest.children}</View>
    </View>
  ) : rest.children
  if (!diagnostics) return <Modal supportedOrientations={supportedOrientations} {...rest}>{children}</Modal>
  return <Modal supportedOrientations={supportedOrientations} {...rest}>{children}{diagnostics}</Modal>
}
